import { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import { useBlogPosts } from '@/hooks/useBlogPosts';
import { SITE_URL } from '@/components/base/Seo';

export default function BlogDetail() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { getPostBySlug, publishedPosts, hydrated } = useBlogPosts();

  const post = useMemo(() => (slug ? getPostBySlug(slug) : undefined), [slug, getPostBySlug]);

  const readingTime = useMemo(() => {
    if (!post?.content) return 0;
    const text = post.content.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    const wordCount = text.split(' ').filter(Boolean).length;
    return Math.max(1, Math.round(wordCount / 200));
  }, [post?.content]);

  const basePath = __BASE_PATH__.split('/').filter(Boolean).join('/');
  const pathPrefix = basePath ? `/${basePath}` : '';
  const canonicalUrl = post ? `${SITE_URL}/blog/${post.slug}` : '';

  // Prefix internal article links so cross-links between posts resolve correctly
  // even when the site is published under a sub-path base.
  const contentHtml = useMemo(() => {
    if (!post?.content) return '';
    if (!pathPrefix) return post.content;
    return post.content
      .replace(/href="\/blog/g, `href="${pathPrefix}/blog`)
      .replace(/href='\/blog/g, `href='${pathPrefix}/blog`);
  }, [post?.content, pathPrefix]);

  const shareText = encodeURIComponent(post?.title || '');
  const shareUrl = encodeURIComponent(canonicalUrl);

  const [copied, setCopied] = useState(false);

  const handleCopyLink = useCallback(() => {
    if (!canonicalUrl) return;
    navigator.clipboard.writeText(canonicalUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [canonicalUrl]);

  // Admin session check — only show edit link for admins
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('admin') === '1') {
      setIsAdmin(true);
      return;
    }
    try {
      const adminSession = localStorage.getItem('blogAdminSession');
      if (adminSession === '1') {
        setIsAdmin(true);
      }
    } catch {
      // localStorage not available
    }
  }, [location.search]);

  useEffect(() => {
    // Wait until posts have finished loading, otherwise a freshly published
    // article would be sent back to the blog list before it can be found.
    if (!hydrated) return;
    if (!post && slug) {
      navigate('/blog', { replace: true });
    }
  }, [post, slug, navigate, hydrated]);

  useEffect(() => {
    if (post) {
      document.title = `${post.title} | Federica Tedeschi Blog`;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', post.excerpt || post.title);
      }
      const metaKeywords = document.querySelector('meta[name="keywords"]');
      if (metaKeywords && post.tags) {
        metaKeywords.setAttribute('content', post.tags.join(','));
      }

      const setProperty = (property: string, content: string) => {
        let meta = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
        if (!meta) {
          meta = document.createElement('meta');
          meta.setAttribute('property', property);
          document.head.appendChild(meta);
        }
        meta.content = content;
      };

      const setName = (name: string, content: string) => {
        let meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
        if (!meta) {
          meta = document.createElement('meta');
          meta.setAttribute('name', name);
          document.head.appendChild(meta);
        }
        meta.content = content;
      };

      const setCanonical = (href: string) => {
        let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
        if (!link) {
          link = document.createElement('link');
          link.rel = 'canonical';
          document.head.appendChild(link);
        }
        link.href = href;
      };

      setCanonical(canonicalUrl);
      setProperty('og:url', canonicalUrl);
      setProperty('og:type', 'article');
      setProperty('og:title', post.title);
      setProperty('og:description', post.excerpt || post.title);
      setProperty('og:image', post.coverImage || 'https://storage.readdy-site.link/project_files/185c4543-d0dc-49d0-8dbb-59fc7deb7f59/4d5c3037-c9af-47f6-bdf8-dc98d98f2d0c_Banner-2-.png?v=9601c2c8a8e1bc8c8627c0876333b127');
      setProperty('article:published_time', post.publishedAt);
      setProperty('article:modified_time', post.updatedAt);
      setProperty('article:author', post.author);
      if (post.category) setProperty('article:section', post.category);
      post.tags?.forEach((tag) => setProperty('article:tag', tag));

      setName('twitter:card', 'summary_large_image');
      setName('twitter:title', post.title);
      setName('twitter:description', post.excerpt || post.title);
      setName('twitter:image', post.coverImage || 'https://storage.readdy-site.link/project_files/185c4543-d0dc-49d0-8dbb-59fc7deb7f59/4d5c3037-c9af-47f6-bdf8-dc98d98f2d0c_Banner-2-.png?v=9601c2c8a8e1bc8c8627c0876333b127');
      setName('twitter:label1', 'Reading time');
      setName('twitter:data1', `${readingTime} min read`);
    }
    return () => {
      document.title = 'Federica Tedeschi | Energy-Safe Transformational Coach for Chronic Fatigue & Pain';
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', 'Energy-Safe Transformational Coach for Chronic Fatigue & Pain. CCE-ICF Accredited.');
      }
      const metaKeywords = document.querySelector('meta[name="keywords"]');
      if (metaKeywords) {
        metaKeywords.setAttribute('content', 'energy-safe coaching,chronic fatigue coach,chronic pain coach,NLP coaching,nervous system regulation');
      }
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.remove();
      document.querySelectorAll('meta[property="og:url"], meta[property="og:type"], meta[property="og:title"], meta[property="og:description"], meta[property="og:image"], meta[property="article:published_time"], meta[property="article:modified_time"], meta[property="article:author"], meta[property="article:section"], meta[property="article:tag"], meta[name="twitter:card"], meta[name="twitter:title"], meta[name="twitter:description"], meta[name="twitter:image"], meta[name="twitter:label1"], meta[name="twitter:data1"]').forEach((el) => el.remove());
    };
  }, [post]);

  if (!post) {
    return null;
  }

  const formattedDate = post.publishedAt
    ? new Date(post.publishedAt).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : '';

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BlogPosting',
            headline: post.title,
            description: post.excerpt || '',
            image: post.coverImage || '',
            datePublished: post.publishedAt,
            dateModified: post.updatedAt,
            author: {
              '@type': 'Person',
              name: post.author,
            },
            publisher: {
              '@type': 'Organization',
              name: 'Healing & Breakthrough',
            },
            url: canonicalUrl,
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': canonicalUrl,
            },
          }),
        }}
      />

      <main>
        {/* Cover Image */}
        {post.coverImage && (
          <div className="relative w-full h-[300px] md:h-[420px] overflow-hidden">
            <img
              src={post.coverImage}
              alt={post.title}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-transparent to-black/20" />
          </div>
        )}

        {/* Article Content */}
        <article className="max-w-3xl mx-auto px-6 py-10 md:py-14">
          {/* Back Link */}
          <a
            href={`${pathPrefix}/blog`}
            className="inline-flex items-center gap-1.5 text-sm text-foreground-500 hover:text-primary-600 transition-colors mb-8 cursor-pointer"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line text-sm" />
            </span>
            Back to Blog
          </a>

          {/* Header */}
          <header className="mb-8">
            {post.category && (
              <span className="inline-block text-xs font-medium px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-800 mb-4">
                {post.category}
              </span>
            )}
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 leading-tight mb-4">
              {post.title}
            </h1>
            <div className="flex items-center gap-3 text-sm text-foreground-500 flex-wrap">
              <span className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                <span className="text-xs font-semibold text-primary-700">
                  {post.author.charAt(0)}
                </span>
              </span>
              <div className="flex flex-col">
                <span className="font-medium text-foreground-700">{post.author}</span>
                <span className="text-xs text-foreground-400">International Coaching Federation (ICF) Certified</span>
              </div>
              <span className="text-foreground-300">·</span>
              <time dateTime={post.publishedAt}>{formattedDate}</time>
              <span className="text-foreground-300">·</span>
              <span className="text-foreground-500">{readingTime} min read</span>
            </div>
          </header>

          {/* Content */}
          <div
            className="blog-content"
            dangerouslySetInnerHTML={{ __html: contentHtml }}
          />

          {/* Share */}
          <div className="mt-10 pt-6 border-t border-background-200/70">
            <p className="text-xs font-medium text-foreground-500 uppercase tracking-wider mb-3">
              Share this article
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={handleCopyLink}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md bg-background-100 text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
                type="button"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className={copied ? 'ri-check-line text-green-600' : 'ri-link'} />
                </span>
                {copied ? 'Copied!' : 'Copy Link'}
              </button>
              <a
                href={`mailto:?subject=${shareText}&body=${shareUrl}`}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md bg-background-100 text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
                rel="nofollow"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-mail-line" />
                </span>
                Email
              </a>
              <a
                href={`https://twitter.com/intent/tweet?text=${shareText}&url=${shareUrl}`}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md bg-background-100 text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-twitter-x-line" />
                </span>
                X
              </a>
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md bg-background-100 text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-facebook-fill" />
                </span>
                Facebook
              </a>
              <a
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md bg-background-100 text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-linkedin-fill" />
                </span>
                LinkedIn
              </a>
            </div>
          </div>

          {/* Tags */}
          {post.tags && post.tags.length > 0 && (
            <div className="mt-10 pt-6 border-t border-background-200/70">
              <div className="flex flex-wrap items-center gap-2">
                {post.tags.map((tag) => (
                  <a
                    key={tag}
                    href={`${pathPrefix}/blog/tag/${encodeURIComponent(tag)}`}
                    className="text-xs px-3 py-1.5 rounded-full bg-background-100 text-foreground-600 border border-background-200/70 transition-colors hover:bg-primary-100 hover:text-primary-700 hover:border-primary-200 cursor-pointer whitespace-nowrap"
                  >
                    #{tag}
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Admin Edit Link — only visible to admins */}
          {isAdmin && (
            <div className="mt-8 pt-6 border-t border-background-200/70">
              <a
                href={`${pathPrefix}/blog/admin?edit=${post.id}`}
                className="inline-flex items-center gap-1.5 text-xs text-foreground-400 hover:text-primary-600 transition-colors cursor-pointer"
              >
                <span className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-edit-line text-xs" />
                </span>
                Edit this post
              </a>
            </div>
          )}
        </article>

        {/* Related Posts */}
        {publishedPosts.length > 1 && (
          <section className="max-w-6xl mx-auto px-6 pb-16">
            <h2 className="font-heading text-2xl font-semibold text-foreground-900 mb-6">
              More Articles
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {publishedPosts
                .filter((p) => p.slug !== post.slug)
                .slice(0, 3)
                .map((relatedPost) => (
                  <a
                    key={relatedPost.id}
                    href={`${pathPrefix}/blog/${relatedPost.slug}`}
                    className="group flex items-start gap-4 p-4 rounded-lg bg-background-50 border border-background-200/70 transition-colors hover:border-primary-200 cursor-pointer"
                  >
                    {relatedPost.coverImage && (
                      <div className="w-20 h-20 rounded-md overflow-hidden flex-shrink-0">
                        <img
                          src={relatedPost.coverImage}
                          alt={relatedPost.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-heading text-sm font-semibold text-foreground-800 leading-snug mb-1 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {relatedPost.title}
                      </h3>
                      {relatedPost.excerpt && (
                        <p className="text-xs text-foreground-500 line-clamp-2">
                          {relatedPost.excerpt}
                        </p>
                      )}
                    </div>
                  </a>
                ))}
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}